"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import DataTable from "@/components/ui/DataTable";
import DrawerPanel from "@/components/ui/DrawerPanel";
import FilterBar from "@/components/ui/FilterBar";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import SearchableSelect from "@/components/ui/SearchableSelect";
import TextArea from "@/components/form/input/TextArea";
import WeightInputPair from "@/components/ui/WeightInputPair";
import { rmcClient } from "@/lib/rmcClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import toast from "react-hot-toast";

function formatWeightTonnes(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(3);
}

function toNumberOrNull(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function formatNetTonnes(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(3)} TON`;
}

export default function RmcGate({ mode }) {
  const inward = mode === "inward";
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [fMat, setFMat] = React.useState(null);
  const [fParty, setFParty] = React.useState(null);
  const [date, setDate] = React.useState(new Date().toISOString().slice(0, 10));
  const [materialId, setMaterialId] = React.useState(null);
  const [partyId, setPartyId] = React.useState(null);
  const [vehicle, setVehicle] = React.useState("");
  const [gross, setGross] = React.useState("0");
  const [tare, setTare] = React.useState("0");
  const [remark, setRemark] = React.useState("");
  const [challanRef, setChallanRef] = React.useState("");

  const { data: materials = [] } = useQuery({
    queryKey: ["materials"],
    queryFn: async () => (await rmcClient.listMaterials()).data,
  });
  const { data: parties = [] } = useQuery({
    queryKey: ["purchaseParties"],
    queryFn: async () => (await rmcClient.listPurchaseParties()).data,
  });
  const { data: rows = [] } = useQuery({
    queryKey: ["gate", mode, fMat, fParty],
    queryFn: async () =>
      inward
        ? (await rmcClient.listGateInward({ material_id: fMat, party_id: fParty })).data
        : (await rmcClient.listGateOutward()).data,
  });

  const submit = useMutation({
    mutationFn: () =>
      inward
        ? rmcClient.createGateInward({
            date,
            material_id: materialId,
            party_id: partyId,
            vehicle_no: vehicle,
            gross,
            tare,
            remark,
          })
        : rmcClient.createGateOutward({
            date,
            challan_ref: challanRef,
            vehicle_no: vehicle,
            gross,
            tare,
            remark,
          }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["gate"] });
      qc.invalidateQueries({ queryKey: ["materials"] });
      toast.success(`${res.sr_no} created`);
      setOpen(false);
      setGross("0");
      setTare("0");
      setRemark("");
      setChallanRef("");
    },
  });

  const g = parseFloat(gross) || 0;
  const t = parseFloat(tare) || 0;
  const weightErr = g <= t ? "Gross weight must be greater than tare weight" : null;

  const partyName = (row) => {
    if (row?.party?.name) return row.party.name;
    const id = row?.party_id;
    return parties.find((p) => p.id === id)?.name || "—";
  };

  const matName = (row) => {
    if (row?.material?.name) return row.material.name;
    const id = row?.material_id;
    return materials.find((m) => m.id === id)?.name || "—";
  };

  const grossValue = (row) => row?.gross_weight ?? row?.gross;
  const tareValue = (row) => row?.tare_weight ?? row?.tare;
  const netValue = (row) => {
    if (row?.net != null) return row.net;
    const gNum = toNumberOrNull(grossValue(row));
    const tNum = toNumberOrNull(tareValue(row));
    if (gNum == null || tNum == null) return null;
    return gNum - tNum;
  };

  return (
    <div className="space-y-6">
      <PageBreadCrumb pageTitle={inward ? "Gate Inward" : "Gate Outward"} />
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setOpen(true)}>
          New Entry
        </Button>
      </div>
      <FilterBar>
        {inward && (
          <>
            <div className="min-w-[160px]">
              <SearchableSelect
                label="Material"
                value={fMat}
                onChange={setFMat}
                options={materials}
              />
            </div>
            <div className="min-w-[160px]">
              <SearchableSelect
                label="Party"
                value={fParty}
                onChange={setFParty}
                options={parties}
              />
            </div>
          </>
        )}
      </FilterBar>
      <DataTable
        columns={
          inward
            ? [
                { key: "sr_no", header: "SR No" },
                { key: "date", header: "Date" },
                { key: "mat", header: "Material", render: (r) => matName(r) },
                { key: "party", header: "Party", render: (r) => partyName(r) },
                { key: "vehicle_no", header: "Vehicle" },
                { key: "gross", header: "Gross", render: (r) => formatWeightTonnes(grossValue(r)) },
                { key: "tare", header: "Tare", render: (r) => formatWeightTonnes(tareValue(r)) },
                { key: "net", header: "Net", render: (r) => formatNetTonnes(netValue(r)) },
                { key: "remark", header: "Remark" },
              ]
            : [
                { key: "sr_no", header: "SR No" },
                { key: "date", header: "Date" },
                { key: "mat", header: "Material", render: () => "—" },
                { key: "party", header: "Party", render: () => "—" },
                { key: "vehicle_no", header: "Vehicle" },
                { key: "gross", header: "Gross", render: (r) => formatWeightTonnes(r.gross) },
                { key: "tare", header: "Tare", render: (r) => formatWeightTonnes(r.tare) },
                { key: "net", header: "Net", render: (r) => formatNetTonnes(r.net) },
                { key: "remark", header: "Remark" },
              ]
        }
        data={rows}
        isLoading={false}
      />

      <DrawerPanel
        open={open}
        onClose={() => setOpen(false)}
        title={inward ? "New inward entry" : "New outward entry"}
        widthClass="max-w-lg"
      >
        <div className="space-y-4">
          <div>
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" />
          </div>
          {!inward && (
            <div>
              <Label>Challan No (optional)</Label>
              <Input value={challanRef} onChange={(e) => setChallanRef(e.target.value)} className="mt-1" />
            </div>
          )}
          {inward && (
            <>
              <SearchableSelect
                label="Material"
                value={materialId}
                onChange={setMaterialId}
                options={materials}
              />
              <SearchableSelect
                label="Party"
                value={partyId}
                onChange={setPartyId}
                options={parties}
              />
            </>
          )}
          <div>
            <Label>Vehicle No</Label>
            <Input value={vehicle} onChange={(e) => setVehicle(e.target.value)} className="mt-1" />
          </div>
          <WeightInputPair
            grossValue={gross}
            tareValue={tare}
            onGrossChange={setGross}
            onTareChange={setTare}
            error={weightErr}
          />
          <div>
            <Label>Remark</Label>
            <TextArea
              value={remark}
              onChange={setRemark}
              rows={2}
              className="mt-1 !text-xs border-gray-800 !bg-gray-950 !text-white rounded-md"
            />
          </div>
          <Button
            size="sm"
            disabled={
              submit.isPending ||
              !!weightErr ||
              !vehicle ||
              (inward && (!materialId || !partyId))
            }
            onClick={() => submit.mutate()}
          >
            Submit
          </Button>
        </div>
      </DrawerPanel>
    </div>
  );
}
